import { Check, Loader, X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { PromotionRequest } from "@/lib/api";
import { formatDateTime, formatRelativeTime } from "@/lib/format";

import type { PendingDecision } from "./types";

type BadgeVariant = React.ComponentProps<typeof Badge>["variant"];

const STATUS: Record<PromotionRequest["status"], { label: string; variant: BadgeVariant }> = {
  pending: { label: "Waiting", variant: "secondary" },
  approved: { label: "Promoted", variant: "default" },
  rejected: { label: "Rejected", variant: "outline" },
  failed: { label: "Failed", variant: "destructive" },
};

interface RequestTableProps {
  requests: PromotionRequest[];
  /** The signed-in admin: their own MCP-raised requests get a check-first hint. */
  currentUserEmail: string | null;
  onDecide: (decision: PendingDecision) => void;
}

export function RequestTable({ requests, currentUserEmail, onDecide }: RequestTableProps) {
  if (requests.length === 0) {
    return (
      <div className="text-muted-foreground py-16 text-center text-sm">
        No promotion requests yet.
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Project</TableHead>
          <TableHead>To</TableHead>
          <TableHead>Requested by</TableHead>
          <TableHead>Reason</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Decision</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {requests.map((request) => (
          <RequestRow
            key={request.id}
            request={request}
            currentUserEmail={currentUserEmail}
            onDecide={onDecide}
          />
        ))}
      </TableBody>
    </Table>
  );
}

function RequestRow({
  request,
  currentUserEmail,
  onDecide,
}: {
  request: PromotionRequest;
  currentUserEmail: string | null;
  onDecide: (decision: PendingDecision) => void;
}) {
  const open = request.status === "pending" || request.status === "failed";
  // Raised by this admin through Claude (MCP): allowed and recorded, but worth
  // a second look before approving -- so only while it is still open. A decided
  // row keeps the plain "via Claude (MCP)" label.
  const raisedByYouThroughClaude =
    open &&
    request.via === "mcp" &&
    currentUserEmail !== null &&
    request.requester_email.toLowerCase() === currentUserEmail.toLowerCase();
  const status = STATUS[request.status];

  return (
    <TableRow>
      <TableCell className="font-medium">{request.project_name}</TableCell>
      <TableCell>
        <Badge variant="outline">{request.target}</Badge>
      </TableCell>
      <TableCell>
        <p className="text-sm">{request.requester_email}</p>
        <p className="text-muted-foreground text-xs" title={formatDateTime(request.created_at)}>
          {formatRelativeTime(request.created_at)}
          {request.via === "mcp" && " · via Claude (MCP)"}
        </p>
        {raisedByYouThroughClaude && (
          <p className="text-xs text-amber-700 dark:text-amber-400">
            You raised this through Claude: check you asked for it before approving.
          </p>
        )}
      </TableCell>
      <TableCell className="max-w-xs text-sm whitespace-normal">
        {request.reason}
        {request.error && <p className="text-destructive mt-1 text-xs">{request.error}</p>}
        {request.decision_note && !open && (
          <p className="text-muted-foreground mt-1 text-xs">
            Note from {request.decided_by}: {request.decision_note}
          </p>
        )}
      </TableCell>
      <TableCell>
        {request.in_progress ? (
          <span className="text-muted-foreground inline-flex items-center gap-1 text-xs">
            <Loader className="size-3 animate-spin" /> Promoting…
          </span>
        ) : (
          <Badge variant={status.variant}>{status.label}</Badge>
        )}
      </TableCell>
      <TableCell className="text-right">
        {open && !request.in_progress ? (
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              title={
                raisedByYouThroughClaude
                  ? "You raised this through Claude (MCP): check you asked for it. The approval is recorded as your own."
                  : undefined
              }
              onClick={() => onDecide({ request, action: "approve" })}
            >
              <Check className="mr-1 size-3.5" />
              {request.status === "failed" ? "Retry" : "Approve"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onDecide({ request, action: "reject" })}
            >
              <X className="mr-1 size-3.5" />
              Reject
            </Button>
          </div>
        ) : (
          <span className="text-muted-foreground text-xs">
            {request.decided_by && !request.in_progress ? request.decided_by : ""}
          </span>
        )}
      </TableCell>
    </TableRow>
  );
}
